import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Box, Divider, Grid, Card, CardMedia, Alert } from '@mui/material';
import { Delete as DeleteIcon, CloudUpload as CloudUploadIcon } from '@mui/icons-material';
import instanceAxios from '@/util/axios';
import { businessAdsAdminUrl, businessAdsUploadUrl } from '@/constants';
import { Button, Chip, Input, Switch, IconButton, Typography, Dialog } from '@sm-dashboard/ui-components';
import AdminCommentsSection from './AdminCommentsSection';
import { IFrontendBusinessAd } from '@/pages/dashboard/BusinessAd/admin/AdminBusinessAdsPage';

const getPaymentPlan = (paymentPlan: string) => {
  if (paymentPlan === 'monthly') return 'חודשי';
  if (paymentPlan === 'half-yearly') return 'חצי שנתי';
  if (paymentPlan === 'yearly') return 'שנתי';
  return 'לא נבחרה';
};

interface ViewAdDialogProps {
  open: boolean;
  onClose: () => void;
  ad: IFrontendBusinessAd | null;
  onAdUpdated?: () => void;
}

const ViewAdDialog: React.FC<ViewAdDialogProps> = ({ open, onClose, ad, onAdUpdated }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editedAd, setEditedAd] = useState<any>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [mainImage, setMainImage] = useState<string>('');
  const [additionalMedia, setAdditionalMedia] = useState<string[]>([]);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const mainImageInputRef = useRef<HTMLInputElement>(null);
  const additionalMediaInputRef = useRef<HTMLInputElement>(null);

  // Update local state when editedAd changes
  useEffect(() => {
    if (editedAd) {
      setMainImage(editedAd.mainImage || '');
      setAdditionalMedia(editedAd.additionalMedia || []);
    }
  }, [editedAd]);

  const currentAd = useMemo(() => (isEditing ? editedAd : ad), [isEditing, editedAd, ad]);
  const paymentPlan = getPaymentPlan(currentAd?.paymentPlan);

  // Memoize the media display
  const displayMainImage = useMemo(() => {
    if (isEditing) {
      return mainImage;
    }
    return currentAd?.mainImage || '';
  }, [isEditing, mainImage, currentAd?.mainImage]);

  const displayAdditionalMedia = useMemo(() => {
    if (isEditing) {
      return additionalMedia;
    }
    return currentAd?.additionalMedia || [];
  }, [isEditing, additionalMedia, currentAd?.additionalMedia]);

  if (!ad) {
    return null;
  }

  const handleEditClick = () => {
    const newEditedAd = { ...ad };
    setEditedAd(newEditedAd);
    setMainImage((newEditedAd as any).mainImage || '');
    setAdditionalMedia((newEditedAd as any).additionalMedia || []);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setEditedAd(null);
    setMainImage('');
    setAdditionalMedia([]);
    setIsEditing(false);
  };

  const handleSave = async () => {
    if (!editedAd) return;

    setSaving(true);
    try {
      // Only send editable fields, exclude metadata
      const updatePayload = {
        businessName: editedAd.businessName,
        businessNameForInvoicing: editedAd.businessNameForInvoicing,
        businessEmail: editedAd.businessEmail,
        businessPhone: editedAd.businessPhone,
        hasWhatsapp: editedAd.hasWhatsapp,
        shortDescription: editedAd.shortDescription,
        fullDescription: editedAd.fullDescription,
        location: editedAd.location,
        minPrice: editedAd.minPrice,
        maxPrice: editedAd.maxPrice,
        targetAudience: editedAd.targetAudience,
        shabadHouseDiscount: editedAd.shabadHouseDiscount,
        mainImage: editedAd.mainImage,
        additionalMedia: editedAd.additionalMedia,
        websiteLink: editedAd.websiteLink,
        facebookLink: editedAd.facebookLink,
        instagramLink: editedAd.instagramLink,
        recommendations: editedAd.recommendations,
        category: editedAd.category,
        subCategory: editedAd.subCategory
      };

      await instanceAxios.put(`${businessAdsAdminUrl}/${ad.id}`, updatePayload);

      // Close the dialog and refresh data
      setIsEditing(false);
      setEditedAd(null);
      setMainImage('');
      setAdditionalMedia([]);

      // Close the dialog
      onClose();

      // Refresh data after a short delay
      setTimeout(() => {
        if (onAdUpdated) {
          onAdUpdated();
        }
      }, 100);
    } catch (error) {
      console.error('Error updating ad:', error);
      alert('שגיאה בשמירת השינויים');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field: string, value: any) => {
    setEditedAd((prev: any) => ({ ...prev, [field]: value }));
  };

  const handleFileUpload = async (file: File, isMainImage: boolean) => {
    setUploading(true);
    setUploadError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await instanceAxios.post(businessAdsUploadUrl, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      const imageUrl = response.data.url;

      if (isMainImage) {
        setMainImage(imageUrl);
        setEditedAd((prev: any) => ({ ...prev, mainImage: imageUrl }));
      } else {
        setAdditionalMedia(prev => {
          const newMedia = [...prev, imageUrl];
          setEditedAd((editedPrev: any) => ({ ...editedPrev, additionalMedia: newMedia }));
          return newMedia;
        });
      }
      // Force re-render is handled by useEffect
    } catch (error) {
      console.error('Error uploading file:', error);
      setUploadError('שגיאה בהעלאת הקובץ');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteMainImage = () => {
    console.log('Deleting main image');
    setMainImage('');
    setEditedAd((prev: any) => ({ ...prev, mainImage: '' }));
  };

  const handleDeleteAdditionalImage = (index: number) => {
    console.log('Deleting additional image at index:', index);
    setAdditionalMedia(prev => {
      const newMedia = prev.filter((_: string, i: number) => i !== index);
      console.log('New media array:', newMedia);
      setEditedAd((editedPrev: any) => ({ ...editedPrev, additionalMedia: newMedia }));
      return newMedia;
    });
  };

  const handleCreatePaymentSession = async () => {
    if (!ad || !ad.id) return;

    setPaymentLoading(true);
    try {
      // Use the admin-specific payment session endpoint
      const response = await instanceAxios.post(`${businessAdsAdminUrl}/${ad.id}/payment-session`);
      console.log('Payment session response:', response.data);

      if (response.data.paymentUrl) {
        // Open CardCom payment page in new window
        window.open(response.data.paymentUrl, '_blank');
        // Note: Webhook will handle saving payment data after actual payment
        // The admin should manually close and reopen the dialog to see updated data
      }
    } catch (error) {
      console.error('Payment session error:', error);
      alert('התרחשה שגיאה ביצירת התשלום. אנא נסה שוב.');
    } finally {
      setPaymentLoading(false);
    }
  };

  const renderMedia = (mediaUrl: string, isMainImage = false, additionalIndex?: number) => {
    if (!mediaUrl || typeof mediaUrl !== 'string') return <Typography variant="body">אין מדיה</Typography>;

    const isImage = /\.(jpeg|jpg|gif|png|webp)(\?.*)?$/i.test(mediaUrl);
    const isVideo =
      /\.(mp4|webm|ogg)(\?.*)?$/i.test(mediaUrl) || mediaUrl.includes('youtube.com') || mediaUrl.includes('vimeo.com');

    if (isImage) {
      return (
        <Card sx={{ maxWidth: 345, position: 'relative' }}>
          <CardMedia
            component="img"
            image={mediaUrl}
            alt="תמונה"
            sx={{
              width: '100%',
              height: 'auto',
              maxHeight: '200px',
              objectFit: 'contain'
            }}
          />
          {isEditing && (
            <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
              <IconButton
                shape="circle"
                color="error"
                size="small"
                aria-label="מחק תמונה"
                onClick={() => {
                  if (isMainImage) {
                    handleDeleteMainImage();
                  } else if (additionalIndex !== undefined) {
                    handleDeleteAdditionalImage(additionalIndex);
                  }
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
        </Card>
      );
    }
    if (isVideo) {
      return (
        <a href={mediaUrl} target="_blank" rel="noopener noreferrer">
          צפה בסרטון
        </a>
      );
    }
    return <Typography variant="body">{mediaUrl}</Typography>;
  };

  return (
    <Dialog
      open={open}
      onClose={isEditing ? undefined : onClose}
      maxWidth="md"
      fullWidth
      title={isEditing ? 'עריכת מודעה' : 'פרטי המודעה'}
      cancelAction={isEditing ? { onClick: handleCancelEdit, disabled: saving } : { label: 'סגור', onClick: onClose }}
      primaryAction={
        isEditing
          ? { label: 'שמור שינויים', onClick: handleSave, loading: saving }
          : { label: 'ערוך מודעה', onClick: handleEditClick }
      }
    >
      <Box sx={{ pt: 1 }}>
        <Box sx={{ mb: 2 }}>
          {isEditing ? (
            <Input
              appearance="plain"
              label="שם העסק"
              value={currentAd?.businessName || ''}
              onChange={e => handleChange('businessName', e.target.value)}
            />
          ) : (
            <Box sx={{ mb: '0.35em' }}>
              <Typography variant="h3">{currentAd?.businessName || ''}</Typography>
            </Box>
          )}
          <Box sx={{ display: 'flex', gap: 1, my: 2 }}>
            <Chip label={currentAd?.submitterType === 'shaliach' ? 'שליח' : 'מפרסם'} variant="primary" size="small" />
            <Chip
              label={currentAd?.status === 'pending' ? 'ממתין' : currentAd?.status === 'approved' ? 'מאושר' : 'דחוי'}
              variant={
                currentAd?.status === 'approved' ? 'success' : currentAd?.status === 'rejected' ? 'error' : 'neutral'
              }
              size="small"
            />
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ mb: '0.35em' }}>
          <Typography variant="h3">פרטים כלליים</Typography>
        </Box>

        {!isEditing && (
          <Typography variant="body">
            <strong>מספר מודעה:</strong> {currentAd?.id || 'לא הוזן'}
          </Typography>
        )}
        {!isEditing && (
          <Typography variant="body">
            <strong>סוג מפרסם:</strong> {currentAd?.submitterType === 'shaliach' ? 'שליח' : 'מפרסם'}
          </Typography>
        )}

        {isEditing ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Input
              appearance="plain"
              label="שם לחשבונית"
              value={currentAd?.businessNameForInvoicing || ''}
              onChange={e => handleChange('businessNameForInvoicing', e.target.value)}
            />
            <Input
              appearance="plain"
              label="אימייל"
              type="email"
              value={currentAd?.businessEmail || ''}
              onChange={e => handleChange('businessEmail', e.target.value)}
            />
            <Input
              appearance="plain"
              label="טלפון"
              value={currentAd?.businessPhone || ''}
              onChange={e => handleChange('businessPhone', e.target.value)}
            />
            <Switch
              label="יש ווטסאפ"
              checked={currentAd?.hasWhatsapp || false}
              onChange={(_e, checked) => handleChange('hasWhatsapp', checked)}
            />
          </Box>
        ) : (
          <>
            <Typography variant="body">
              <strong>שם לחשבונית:</strong> {currentAd?.businessNameForInvoicing || 'לא הוזן'}
            </Typography>
            <Typography variant="body">
              <strong>אימייל:</strong> {currentAd?.businessEmail || 'לא הוזן'}
            </Typography>
            <Typography variant="body">
              <strong>טלפון:</strong> {currentAd?.businessPhone || 'לא הוזן'}
            </Typography>
            <Typography variant="body">
              <strong>יש ווטסאפ:</strong> {currentAd?.hasWhatsapp ? 'כן' : 'לא'}
            </Typography>
          </>
        )}

        <Divider sx={{ my: 2 }} />

        {isEditing ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Input
              appearance="plain"
              label="תיאור קצר"
              value={currentAd?.shortDescription || ''}
              onChange={e => handleChange('shortDescription', e.target.value)}
              multiline
              rows={2}
            />
            <Input
              appearance="plain"
              label="תיאור מלא"
              value={currentAd?.fullDescription || ''}
              onChange={e => handleChange('fullDescription', e.target.value)}
              multiline
              rows={4}
            />
          </Box>
        ) : (
          <>
            <Typography variant="body">
              <strong>תיאור קצר:</strong> {currentAd?.shortDescription || 'לא הוזן'}
            </Typography>
            <Typography variant="body">
              <strong>תיאור מלא:</strong> {currentAd?.fullDescription || 'לא הוזן'}
            </Typography>
          </>
        )}

        <Divider sx={{ my: 2 }} />

        {isEditing ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Input
              appearance="plain"
              label="קטגוריה"
              value={currentAd?.category || ''}
              onChange={e => handleChange('category', e.target.value)}
            />
            <Input
              appearance="plain"
              label="תת קטגוריה (מופרדות בפסיק)"
              value={currentAd?.subCategory?.join(', ') || ''}
              onChange={e =>
                handleChange(
                  'subCategory',
                  e.target.value.split(',').map((s: string) => s.trim())
                )
              }
              helperText="הפרד תתי קטגוריות בפסיק"
            />
          </Box>
        ) : (
          <>
            <Typography variant="body">
              <strong>קטגוריה:</strong> {currentAd?.category || 'לא הוזן'}
            </Typography>
            <Typography variant="body">
              <strong>תת קטגוריה:</strong>{' '}
              {currentAd?.subCategory && currentAd.subCategory.length > 0
                ? currentAd.subCategory.join(', ')
                : 'לא הוזן'}
            </Typography>
          </>
        )}

        <Divider sx={{ my: 2 }} />

        {isEditing ? (
          <Input
            appearance="plain"
            label="מיקום (מופרד בפסיק)"
            value={currentAd?.location?.join(', ') || ''}
            onChange={e =>
              handleChange(
                'location',
                e.target.value.split(',').map((s: string) => s.trim())
              )
            }
            helperText="הפרד מיקומים בפסיק"
          />
        ) : (
          <Typography variant="body">
            <strong>מיקום:</strong>{' '}
            {currentAd?.location && currentAd.location.length > 0 ? currentAd.location.join(', ') : 'לא הוזן'}
          </Typography>
        )}

        <Divider sx={{ my: 2 }} />

        {isEditing ? (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Input
              appearance="plain"
              label="מחיר מינימום"
              type="number"
              value={currentAd?.minPrice || ''}
              onChange={e => handleChange('minPrice', e.target.value ? Number(e.target.value) : undefined)}
            />
            <Input
              appearance="plain"
              label="מחיר מקסימום"
              type="number"
              value={currentAd?.maxPrice || ''}
              onChange={e => handleChange('maxPrice', e.target.value ? Number(e.target.value) : undefined)}
            />
          </Box>
        ) : (
          <Typography variant="body">
            <strong>טווח מחירים:</strong>{' '}
            {currentAd?.minPrice || currentAd?.maxPrice
              ? currentAd.minPrice && currentAd.maxPrice
                ? `₪${currentAd.minPrice} - ₪${currentAd.maxPrice}`
                : currentAd.minPrice
                ? `החל מ-₪${currentAd.minPrice}`
                : `עד ₪${currentAd.maxPrice}`
              : 'לא הוזן'}
          </Typography>
        )}

        <Divider sx={{ my: 2 }} />

        {isEditing ? (
          <Input
            appearance="plain"
            label="קהל יעד (מופרד בפסיק)"
            value={currentAd?.targetAudience?.join(', ') || ''}
            onChange={e =>
              handleChange(
                'targetAudience',
                e.target.value.split(',').map((s: string) => s.trim())
              )
            }
            helperText="הפרד קהלי יעד בפסיק"
          />
        ) : (
          <Typography variant="body">
            <strong>קהל יעד:</strong>{' '}
            {currentAd?.targetAudience && currentAd.targetAudience.length > 0
              ? currentAd.targetAudience.join(', ')
              : 'לא הוזן'}
          </Typography>
        )}

        <Divider sx={{ my: 2 }} />

        {isEditing ? (
          <Input
            appearance="plain"
            label='הנחה לבתי חב"ד'
            value={currentAd?.shabadHouseDiscount || ''}
            onChange={e => handleChange('shabadHouseDiscount', e.target.value)}
          />
        ) : (
          <Typography variant="body">
            <strong>הנחה לבתי חב&quot;ד:</strong> {currentAd?.shabadHouseDiscount || 'לא הוזן'}
          </Typography>
        )}

        <Divider sx={{ my: 2 }} />

        {isEditing ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Input
              appearance="plain"
              label="אתר"
              value={currentAd?.websiteLink || ''}
              onChange={e => handleChange('websiteLink', e.target.value)}
            />
            <Input
              appearance="plain"
              label="פייסבוק"
              value={currentAd?.facebookLink || ''}
              onChange={e => handleChange('facebookLink', e.target.value)}
            />
            <Input
              appearance="plain"
              label="אינסטגרם"
              value={currentAd?.instagramLink || ''}
              onChange={e => handleChange('instagramLink', e.target.value)}
            />
          </Box>
        ) : (
          <>
            <Typography variant="body">
              <strong>אתר:</strong>{' '}
              {currentAd?.websiteLink ? (
                <a href={currentAd.websiteLink} target="_blank" rel="noopener noreferrer">
                  {currentAd.websiteLink}
                </a>
              ) : (
                'לא הוזן'
              )}
            </Typography>
            <Typography variant="body">
              <strong>פייסבוק:</strong>{' '}
              {currentAd?.facebookLink ? (
                <a href={currentAd.facebookLink} target="_blank" rel="noopener noreferrer">
                  {currentAd.facebookLink}
                </a>
              ) : (
                'לא הוזן'
              )}
            </Typography>
            <Typography variant="body">
              <strong>אינסטגרם:</strong>{' '}
              {currentAd?.instagramLink ? (
                <a href={currentAd.instagramLink} target="_blank" rel="noopener noreferrer">
                  {currentAd.instagramLink}
                </a>
              ) : (
                'לא הוזן'
              )}
            </Typography>
          </>
        )}

        <Divider sx={{ my: 2 }} />

        {isEditing ? (
          <Input
            appearance="plain"
            label="המלצות"
            value={currentAd?.recommendations || ''}
            onChange={e => handleChange('recommendations', e.target.value)}
            multiline
            rows={3}
          />
        ) : (
          <Typography variant="body">
            <strong>המלצות:</strong> {currentAd?.recommendations || 'לא הוזן'}
          </Typography>
        )}

        {!isEditing && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="body">
              <strong>תכנית תשלום:</strong> {paymentPlan}
            </Typography>
            {currentAd?.submitterType === 'publisher' && (
              <>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ mb: '0.35em' }}>
                  <Typography variant="h3">פרטי תשלום</Typography>
                </Box>
                {currentAd?.paymentSessionData ? (
                  <Box>
                    {currentAd.paymentSessionData.documentUrl && (
                      <Typography variant="body">
                        <strong>Document URL:</strong>{' '}
                        <a href={currentAd.paymentSessionData.documentUrl} target="_blank" rel="noopener noreferrer">
                          צפה בקובץ
                        </a>
                      </Typography>
                    )}
                    <Button
                      variant="outlined"
                      onClick={handleCreatePaymentSession}
                      loading={paymentLoading}
                      fullWidth={false}
                    >
                      עדכן פרטי תשלום
                    </Button>
                  </Box>
                ) : (
                  <Box>
                    <Typography color="text.secondary">לא הוזנו פרטי תשלום</Typography>
                    <Box sx={{ mt: 2 }}>
                      <Button onClick={handleCreatePaymentSession} loading={paymentLoading} fullWidth={false}>
                        יצירת תשלום
                      </Button>
                    </Box>
                  </Box>
                )}
              </>
            )}
          </>
        )}

        {/* Media Section */}
        <Divider sx={{ my: 2 }} />
        <Box sx={{ mb: '0.35em' }}>
          <Typography variant="h3">מדיה</Typography>
        </Box>

        {uploadError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setUploadError(null)}>
            {uploadError}
          </Alert>
        )}

        <Grid container spacing={2} key={`media-${mainImage}-${additionalMedia.length}`}>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="subtitle2">תמונה ראשית</Typography>
            {displayMainImage ? (
              renderMedia(displayMainImage, true)
            ) : (
              <Typography variant="body">אין תמונה ראשית</Typography>
            )}
            {isEditing &&
              (() => {
                console.log('Rendering main image:', displayMainImage);
                return null;
              })()}
            {isEditing && (
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<CloudUploadIcon />}
                  disabled={uploading}
                  loading={uploading}
                  fullWidth
                  onClick={() => mainImageInputRef.current?.click()}
                >
                  {mainImage ? 'החלף תמונה' : 'העלה תמונה'}
                </Button>
                <input
                  ref={mainImageInputRef}
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={e => {
                    const file = e.target.files?.[0];
                    if (file) handleFileUpload(file, true);
                    e.target.value = '';
                  }}
                />
              </Box>
            )}
          </Grid>
          <Grid item xs={12} sm={6} md={8}>
            <Typography variant="subtitle2">מדיה נוספת</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
              {displayAdditionalMedia.length > 0 ? (
                displayAdditionalMedia.map((media: string, index: number) => (
                  <div key={index}>{renderMedia(media, false, index)}</div>
                ))
              ) : (
                <Typography variant="body">לא הועלתה מדיה נוספת</Typography>
              )}
              {isEditing &&
                (() => {
                  console.log('Rendering additional media:', displayAdditionalMedia);
                  return null;
                })()}
            </Box>
            {isEditing && (
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<CloudUploadIcon />}
                  disabled={uploading}
                  loading={uploading}
                  fullWidth={false}
                  onClick={() => additionalMediaInputRef.current?.click()}
                >
                  הוסף תמונה
                </Button>
                <input
                  ref={additionalMediaInputRef}
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={e => {
                    const file = e.target.files?.[0];
                    if (file) handleFileUpload(file, false);
                    e.target.value = '';
                  }}
                />
              </Box>
            )}
          </Grid>
        </Grid>

        {/* Comments Section - Only in view mode */}
        {!isEditing && (
          <>
            <Divider sx={{ my: 3 }} />
            <AdminCommentsSection adId={currentAd?.id} />
          </>
        )}
      </Box>
    </Dialog>
  );
};

export default ViewAdDialog;
